import { forwardRef, useImperativeHandle } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { checkCondition2 } from '@/shared/_legacy_/condition-utils';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { getSelectLanguagesOptions } from '@/shared/dictionaries/languages';
import { OmittedBaseDto, TDtoWrapper, ValueType } from '@/shared/lib/forms/form-wrapper.types';
import {
  LegalizationType,
  LegalizationTypeList,
  LegalizedCopyDto,
  StorageLocationList,
  StorageLocationType,
} from '@/shared/lib/sj-orm';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';
import { Icon } from '@/shared/uikit/Icon';
import { Button } from '@/shared/uikitV2/Button';

import { useDynamicFields } from '../hooks/useDynamicFields';

import { RenderQuestionComponent } from './RenderQuestionsComponent';

type LegalizedCopyHelp = {
  legalizationType: QuestionType.SELECT_FROM_LIST;
  otherLegalizationType: QuestionType.TEXT;
  languageOfCopy: QuestionType.SELECT_FROM_LIST;
  numberOfCopies: QuestionType.NUMBER;
  copyStorageLocation: QuestionType.SELECT_FROM_LIST;
  otherCopyStorageLocation: QuestionType.TEXT;
  copyStorageComments: QuestionType.TEXT_MULTILINE;
  // copyScan: QuestionType.FILE_UPLOAD;
};

export type FieldsArrayRef = {
  handleAdd?: () => void;
};

const questions: TDtoWrapper<LegalizedCopyDto, LegalizedCopyHelp> = {
  legalizationType: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:legalizedCopies.inputs.legalizationType.label',
    items: LegalizationTypeList,
  },
  otherLegalizationType: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:legalizedCopies.inputs.otherLegalizationType.label',
    disableIf: {
      legalizationType: {
        value: LegalizationType.OTHER,
      },
    },
  },
  languageOfCopy: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:legalizedCopies.inputs.languageOfCopy.label',
    items: getSelectLanguagesOptions(),
    withSearch: true,
  },
  numberOfCopies: {
    type: QuestionType.NUMBER,
    label: 'forms_personal_ids:legalizedCopies.inputs.numberOfCopies.label',
  },
  copyStorageLocation: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_personal_ids:legalizedCopies.inputs.copyStorageLocation.label',
    items: StorageLocationList,
  },
  otherCopyStorageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_personal_ids:legalizedCopies.inputs.otherCopyStorageLocation.label',
    disableIf: {
      copyStorageLocation: {
        value: StorageLocationType.OTHER,
      },
    },
  },
  copyStorageComments: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_personal_ids:legalizedCopies.inputs.copyStorageComments.label',
    colSpan: 2,
  },
};

type LegalizedCopiesFieldProps = {
  name: string;
};

export const LegalizedCopiesField = forwardRef<FieldsArrayRef, LegalizedCopiesFieldProps>(
  ({ name }, ref) => {
    const { t: tc } = useTranslation('common');
    const { t: tf } = useTranslation('forms_personal_ids');
    const { control } = useFormContext();
    const { fields, handleAdd, remove } = useDynamicFields({ name, control, items: questions });

    const rows = useWatch({ control, name });

    useImperativeHandle(ref, () => ({
      handleAdd,
    }));

    return (
      <ItemsContainer>
        {fields.map((field, index) => {
          const rowValues = rows?.[index];

          return (
            <Item key={field.id}>
              {Object.entries(questions).map(([key, value]) => {
                const question = value as ValueType<
                  QuestionType,
                  TDtoWrapper<OmittedBaseDto, Record<string, QuestionType>>
                >;

                const fieldOptions =
                  question.type === QuestionType.SELECT_FROM_LIST
                    ? question.items.map((option) => ({
                        value: option.key,
                        content: { label: tf(option.label) },
                      }))
                    : undefined;

                const disabled =
                  // @ts-ignore refactor checkCondition2
                  question.disableIf && !checkCondition2(rowValues, question.disableIf);

                return (
                  <RenderQuestionComponent
                    key={field.id}
                    id={`${name}.${index}.${key}`}
                    label={question.label}
                    question={question}
                    options={fieldOptions}
                    disabled={disabled}
                  />
                );
              })}

              <DeleteButton
                size="small"
                type="text"
                icon={<Icon name="trash" size="s20" />}
                disabled={fields.length <= 1}
                onClick={() => remove(index)}
              >
                {tc('delete')}
              </DeleteButton>
            </Item>
          );
        })}
      </ItemsContainer>
    );
  },
);

LegalizedCopiesField.displayName = 'LegalizedCopiesField';

const ItemsContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${media.isMobile} {
    gap: 32px;
  }
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  border: 1px solid ${COLORS.systemLight300};
  border-radius: 12px;
  padding: 16px;
`;

const DeleteButton = styled(Button)`
  &&& {
    justify-self: start;
    height: 20px;
    color: ${COLORS.systemFlame200};

    &:hover:not(:disabled) {
      color: ${COLORS.systemFlame300} !important;
    }
  }
`;
